import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Page } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Callout } from '../components/ui/Callout';
import { Field } from '../components/ui/Field';
import { Textarea } from '../components/ui/Input';
import { Stepper } from '../components/ui/Stepper';
import { Icon } from '../components/ui/Icon';
import { Dialog } from '../components/ui/radix/Dialog';

const STEPS = ['Quote', 'Shipping', 'Documents', 'Confirmation'];

const generateTrackingNumber = () => {
  const prefix = 'GSS';
  const randomDigits = Math.floor(10000000 + Math.random() * 90000000);
  return `${prefix}${randomDigits}`;
};

const formatLongDate = (date: Date) =>
  date.toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const ShippingConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state as any;

  const display = useMemo(() => {
    if (!orderData)
      return {
        origin: 'Unknown',
        destination: 'Unknown',
        shippingType: 'package',
        rate: {
          carrier: 'Carrier',
          service: 'Standard',
          deliveryTime: 'TBD',
          price: 0,
          currency: 'ILS',
          baseCost: 0,
          tax: 0,
          handlingFee: 0,
        },
        trackingNumber: '',
        orderNumber: '',
        referenceNumber: '',
        paymentMethod: 'card',
        senderEmail: '',
      };

    const fromAddress =
      orderData.fromAddress ||
      orderData.orderSummary?.sender ||
      { country: { name: 'Unknown' }, email: '' };
    const toAddress =
      orderData.toAddress ||
      orderData.orderSummary?.recipient ||
      { country: { name: 'Unknown' } };

    return {
      origin: fromAddress.country?.name || fromAddress.country || 'Unknown',
      destination: toAddress.country?.name || toAddress.country || 'Unknown',
      shippingType:
        (orderData.isDocument ?? orderData.orderSummary?.isDocument) ? 'document' : 'package',
      rate: {
        carrier:
          orderData.carrier ||
          orderData.provider ||
          orderData.selectedRate?.carrier ||
          orderData.orderSummary?.carrier ||
          'Express Delivery',
        service:
          orderData.service ||
          orderData.selectedRate?.name ||
          orderData.orderSummary?.service ||
          'Standard Service',
        deliveryTime:
          orderData.deliveryEstimate ||
          orderData.selectedRate?.deliveryTime ||
          orderData.orderSummary?.deliveryEstimate ||
          'Delivery time not available',
        price:
          orderData.totalPrice ||
          orderData.selectedRate?.price ||
          orderData.orderSummary?.price?.total ||
          0,
        currency:
          orderData.currency ||
          orderData.selectedRate?.currency ||
          orderData.orderSummary?.currency ||
          'ILS',
        baseCost: orderData.orderSummary?.price?.shipping || 0,
        tax: orderData.orderSummary?.price?.tax || 0,
        handlingFee: orderData.orderSummary?.price?.insurance || 0,
      },
      trackingNumber: orderData.trackingNumber || '',
      orderNumber: orderData.orderNumber || '',
      referenceNumber:
        orderData.referenceNumber || orderData.orderSummary?.referenceNumber || '',
      paymentMethod: orderData.paymentMethod || 'card',
      senderEmail: fromAddress.email || '',
    };
  }, [orderData]);

  const [trackingNumber] = useState<string>(
    display.trackingNumber || generateTrackingNumber()
  );
  const [copied, setCopied] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState<number | null>(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);

  const eta = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d;
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(trackingNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTrack = () => {
    navigate(`/track/${trackingNumber}`);
  };

  const handleSubmitFeedback = () => {
    console.log('Feedback submitted:', {
      rating: feedbackRating,
      comment: feedbackComment,
    });
    setFeedbackSent(true);
    setTimeout(() => {
      setFeedbackOpen(false);
      setFeedbackSent(false);
      setFeedbackRating(null);
      setFeedbackComment('');
    }, 1500);
  };

  return (
    <Page width="narrow">
      <div style={{ marginBottom: 24 }}>
        <Stepper
          steps={STEPS.map((label, i) => ({
            label,
            status: (i < 3 ? 'done' : 'active') as 'done' | 'active',
          }))}
        />
      </div>

      <Card
        style={{
          padding: 40,
          textAlign: 'center',
          background: 'linear-gradient(180deg, var(--bg-elev), var(--bg-tint))',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'var(--ok)',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <Icon name="check" size={32} color="#fff" strokeWidth={2.5} />
        </div>
        <h1 style={{ fontSize: 28, letterSpacing: '-0.02em' }}>Shipment booked!</h1>
        <p className="muted" style={{ marginTop: 6 }}>
          Your label is ready. {display.senderEmail && (
            <>We've emailed a copy to <strong style={{ color: 'var(--ink)' }}>{display.senderEmail}</strong>.</>
          )}
        </p>
        <div
          className="mono"
          style={{
            marginTop: 24,
            fontSize: 14,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 99,
          }}
        >
          <Icon name="barcode" size={14} />
          {trackingNumber}
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={handleCopy}
            aria-label="Copy tracking number"
          >
            <Icon name={copied ? 'check' : 'copy'} size={12} />
          </button>
        </div>
        <div style={{ marginTop: 12 }}>
          <Badge tone="ok">
            Estimated delivery · {formatLongDate(eta)}
          </Badge>
        </div>
      </Card>

      <div className="grid-2" style={{ marginTop: 20 }}>
        <Card pad>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: 15 }}>Shipping label</h3>
            <Badge tone="ok">Ready</Badge>
          </div>
          <div className="ph" style={{ height: 160, marginTop: 14 }}>
            PDF · 4×6 · SHIPPING LABEL
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <Button
              variant="primary"
              size="sm"
              block
              onClick={() => navigate('/orders')}
            >
              <Icon name="download" size={14} />
              <span>View in orders</span>
            </Button>
            <Button size="sm" onClick={handleTrack}>
              <Icon name="route" size={14} />
              <span>Track</span>
            </Button>
          </div>
        </Card>

        <Card pad>
          <h3 style={{ fontSize: 15 }}>What happens next</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
            {[
              { t: 'Print the label', d: '4×6 thermal or A4. Attach to the largest face of the box.' },
              { t: 'Drop off or schedule pickup', d: `${display.rate.carrier} drop-off, or book a pickup.` },
              { t: 'Track automatically', d: 'Live updates in your dashboard and by email.' },
            ].map((s, i) => (
              <div key={i} className="row gap-3" style={{ alignItems: 'flex-start' }}>
                <div
                  className="mono"
                  style={{
                    fontSize: 13,
                    color: 'var(--brand-blue)',
                    fontWeight: 700,
                    minWidth: 20,
                  }}
                >
                  {i + 1}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{s.t}</div>
                  <div className="muted" style={{ fontSize: 13 }}>{s.d}</div>
                </div>
              </div>
            ))}
          </div>
          <Button block size="sm" style={{ marginTop: 14 }}>
            <Icon name="calendar" size={14} />
            <span>Schedule pickup</span>
          </Button>
        </Card>
      </div>

      <Card pad style={{ marginTop: 20 }}>
        <h3 style={{ fontSize: 15, marginBottom: 14 }}>Shipment summary</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {[
            ['From', display.origin],
            ['To', display.destination],
            ['Carrier', display.rate.carrier],
            ['Service', display.rate.service],
            ['Type', display.shippingType === 'document' ? 'Document' : 'Package'],
            ['Delivery', display.rate.deliveryTime],
            ['Order #', display.orderNumber || '—'],
            ['Reference', display.referenceNumber || '—'],
          ].map(([label, value]) => (
            <div key={label}>
              <div
                className="mono"
                style={{ fontSize: 11, color: 'var(--ink-4)', letterSpacing: '0.06em' }}
              >
                {label.toUpperCase()}
              </div>
              <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2 }}>{value}</div>
            </div>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginTop: 18,
            paddingTop: 14,
            borderTop: '1px solid var(--line)',
          }}
        >
          <span style={{ fontWeight: 600 }}>Total paid</span>
          <span
            className="mono"
            style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}
          >
            {display.rate.price} {display.rate.currency}
          </span>
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button variant="ghost" onClick={handleTrack}>
          <Icon name="route" size={14} />
          <span>Track shipment</span>
        </Button>
        <Button variant="ghost" onClick={() => navigate('/orders')}>
          <Icon name="package" size={14} />
          <span>View all orders</span>
        </Button>
        <Button variant="ghost" onClick={() => setFeedbackOpen(true)}>
          <Icon name="star" size={14} />
          <span>Leave feedback</span>
        </Button>
        <Button variant="blue" onClick={() => navigate('/shipping')}>
          <Icon name="plus" size={14} />
          <span>New shipment</span>
        </Button>
      </div>

      <Dialog
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        title="Share your feedback"
        description="How was your shipping experience?"
        maxWidth={480}
      >
        {feedbackSent ? (
          <Callout tone="ok">
            <Icon name="checkCircle" size={16} />
            <span>Thank you for your feedback!</span>
          </Callout>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              {[1, 2, 3, 4, 5].map((v) => (
                <button
                  key={v}
                  type="button"
                  className="btn btn-ghost btn-icon"
                  onClick={() => setFeedbackRating(v)}
                  aria-label={`Rate ${v} stars`}
                >
                  <Icon
                    name="star"
                    size={20}
                    color={
                      feedbackRating !== null && feedbackRating >= v
                        ? 'var(--brand-amber)'
                        : 'var(--ink-4)'
                    }
                  />
                </button>
              ))}
            </div>
            <Field label="Comment (optional)">
              <Textarea
                rows={4}
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                placeholder="Tell us what worked and what could be better…"
              />
            </Field>
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}
            >
              <Button onClick={() => setFeedbackOpen(false)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleSubmitFeedback}
                disabled={feedbackRating === null}
              >
                <Icon name="send" size={14} />
                <span>Submit</span>
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </Page>
  );
};

export default ShippingConfirmationPage;
